import React, { useEffect, useState, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import useDeduplicatedSubscription from '/imports/ui/core/hooks/useDeduplicatedSubscription';
import CURRENT_USER_SUBSCRIPTION from '/imports/ui/core/graphql/queries/currentUserSubscription';
import { GET_MEETING_RECORDING_DATA } from '/imports/ui/components/nav-bar/nav-bar-graphql/recording-indicator/queries';
import autoScreenshareService from '/imports/ui/services/auto-screenshare';
import { useIsSharing, isSharing as isSharingFn } from '/imports/ui/components/screenshare/service';
import logger from '/imports/startup/client/logger';
import { courseSelect } from '../app/context';
// [Fix N7] Removed unused usePresentationSwap import
import useMeeting from '/imports/ui/core/hooks/useMeeting';
import { screenshareHasEnded } from '/imports/ui/components/screenshare/service';

const AutoScreenshareHandler: React.FC = () => {
  const { data: currentUserData } = useDeduplicatedSubscription(CURRENT_USER_SUBSCRIPTION);
  const { data: recordingData } = useDeduplicatedSubscription(GET_MEETING_RECORDING_DATA);
  const { data: currentMeeting } = useMeeting((m) => ({
    componentsFlags: m.componentsFlags,
  }));
  // [Fix N1] hasAttemptedScreenshare is now tracked in the service singleton
  // so it survives component remounts (e.g., presenter role flicker).
  const [previousRecordingStatus, setPreviousRecordingStatus] = useState(false);
  // [Fix #17] Track whether recording status has been initialized to avoid
  // triggering screenshare prompt on late-join when recording is already active
  const hasInitializedRecording = useRef(false);
  // [Fix N4] Removed local isProcessingScreenshare — use service.isProcessing() instead.
  const [previousScreenshareStatus, setPreviousScreenshareStatus] = useState(false);
  const [previousHasExternalVideo, setPreviousHasExternalVideo] = useState(false);

  const apolloClient = useApolloClient();
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stopDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // [Fix #4] Keep a ref to the current user so debounced callbacks read fresh data
  const userRef = useRef<any>(null);

  // Listen to screenshare state changes
  const isSharing = useIsSharing();

  // Get course context to check isPrepareLesson
  const courseDetails = courseSelect((state: any) => state);
  const isPrepareLesson = courseDetails?.course?.courseDetail?.isPrepareLesson ?? false;

  // Track prepare→live transitions to sync stale state without triggering actions
  const wasPrepareShareRef = useRef(isPrepareLesson);
  const wasPrepareRecordingRef = useRef(isPrepareLesson);

  useEffect(() => {
    // Set Apollo Client in the service
    autoScreenshareService.setApolloClient(apolloClient);
  }, [apolloClient]);

  // Initialize previousHasExternalVideo when currentMeeting data becomes available
  useEffect(() => {
    if (currentMeeting?.componentsFlags?.hasExternalVideo !== undefined) {
      setPreviousHasExternalVideo(currentMeeting.componentsFlags.hasExternalVideo);
    }
  }, [currentMeeting?.componentsFlags?.hasExternalVideo]);

  useEffect(() => {
    const user = currentUserData?.user_current?.[0];

    logger.debug({
      logCode: 'screenshare_handler_join_effect',
      extraInfo: {
        hasUser: !!user,
        isModerator: user?.isModerator,
        joined: user?.joined,
        enabled: autoScreenshareService.isEnabled(),
        hasAttempted: autoScreenshareService.hasAttemptedJoinScreenshare,
        isProcessing: autoScreenshareService.isProcessing(),
        isPrepareLesson,
      },
    }, `[SCREENSHARE DEBUG] Join effect — user=${user?.name}, mod=${user?.isModerator}, joined=${user?.joined}, attempted=${autoScreenshareService.hasAttemptedJoinScreenshare}`);

    if (!user || !user.isModerator || !user.joined) return;
    if (!autoScreenshareService.isEnabled()) return;
    // [Fix N1] Check service-level flag instead of local state
    if (autoScreenshareService.hasAttemptedJoinScreenshare || autoScreenshareService.isProcessing()) return;

    // [Fix] Mark attempt in service BEFORE the isPrepareLesson check.
    // This prevents a race condition where:
    //   1. Tutor joins in prepare mode → isPrepareLesson=true → early return
    //   2. Tutor clicks "Start Lesson" → isPrepareLesson flips to false
    //   3. Join effect re-runs and auto-screenshare fires without prompting
    // By marking the attempt here, the re-run on step 3 is blocked.
    autoScreenshareService.hasAttemptedJoinScreenshare = true;

    // Only proceed if isPrepareLesson is false
    if (isPrepareLesson) {
      logger.info({
        logCode: 'auto_screenshare_skipped_prepare_lesson',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          isPrepareLesson,
        },
      }, `Auto screenshare skipped for moderator: ${user.name} - isPrepareLesson is true`);
      return;
    }

    // Clear any existing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    processingTimeoutRef.current = setTimeout(() => {
      autoScreenshareService.handleUserJoin(user);
    }, 100);
  }, [currentUserData, isPrepareLesson]);

  // Monitor recording status changes
  // [Fix N2/N3] This is the SOLE entry point for recording→screenshare coordination.
  // The RecordingContainer no longer calls handleRecordingToggle directly.
  useEffect(() => {
    const user = currentUserData?.user_current?.[0];
    if (!user || !user.isModerator) return;
    // [Fix N4] Use service.isProcessing() instead of local state
    if (autoScreenshareService.isProcessing()) return;

    if (isPrepareLesson) {
      wasPrepareRecordingRef.current = true;
      return;
    }

    const currentRecordingStatus = recordingData?.meeting_recording?.[0]?.isRecording ?? false;

    // Sync stale state on prepare→live transition without triggering actions
    if (wasPrepareRecordingRef.current) {
      wasPrepareRecordingRef.current = false;
      logger.info({
        logCode: 'screenshare_handler_recording_prepare_transition_sync',
        extraInfo: { previousRecordingStatus, currentRecordingStatus },
      }, `[SCREENSHARE DEBUG] Prepare→live transition: syncing previousRecordingStatus ${previousRecordingStatus} → ${currentRecordingStatus}`);
      setPreviousRecordingStatus(currentRecordingStatus);
      return;
    }

    logger.debug({
      logCode: 'screenshare_handler_recording_effect',
      extraInfo: {
        currentRecordingStatus,
        previousRecordingStatus,
        hasInitialized: hasInitializedRecording.current,
        isProcessing: autoScreenshareService.isProcessing(),
      },
    }, `[SCREENSHARE DEBUG] Recording effect — current=${currentRecordingStatus}, prev=${previousRecordingStatus}, initialized=${hasInitializedRecording.current}`);

    // [Fix #17] On first run, just sync state without triggering a toggle.
    // This prevents an unexpected screenshare prompt when joining a meeting
    // where recording is already active.
    if (!hasInitializedRecording.current) {
      hasInitializedRecording.current = true;
      setPreviousRecordingStatus(currentRecordingStatus);
      return;
    }

    // Only handle recording toggle if status actually changed
    if (currentRecordingStatus !== previousRecordingStatus) {
      setPreviousRecordingStatus(currentRecordingStatus);

      // Always handle recording toggle - the service will manage the screenshare/recording relationship
      autoScreenshareService.handleRecordingToggle(currentRecordingStatus, user);
    }
  }, [recordingData, currentUserData, previousRecordingStatus, isPrepareLesson]);

  // [Fix #4] Keep userRef in sync so debounced callbacks read fresh data
  const currentUser = currentUserData?.user_current?.[0];
  useEffect(() => {
    userRef.current = currentUser ?? null;
  }, [currentUser]);

  // Monitor screenshare state changes to stop recording when screenshare is manually stopped
  // Debounces true→false transitions by 500ms to avoid false triggers during WebRTC reconnection
  useEffect(() => {
    const user = currentUser;
    if (!user || !user.isModerator) return;
    // [Fix N4] Use service.isProcessing()
    if (autoScreenshareService.isProcessing()) return;
    if (isPrepareLesson) {
      wasPrepareShareRef.current = true;
      return;
    }

    // Sync stale state on prepare→live transition without triggering actions
    if (wasPrepareShareRef.current) {
      wasPrepareShareRef.current = false;
      if (previousScreenshareStatus !== isSharing) {
        logger.info({
          logCode: 'screenshare_handler_prepare_transition_sync',
          extraInfo: { previousScreenshareStatus, isSharing },
        }, `[SCREENSHARE DEBUG] Prepare→live transition: syncing previousScreenshareStatus ${previousScreenshareStatus} → ${isSharing}`);
        setPreviousScreenshareStatus(isSharing);
      }
      return;
    }

    logger.debug({
      logCode: 'screenshare_handler_sharing_effect',
      extraInfo: {
        isSharing,
        previousScreenshareStatus,
        isProcessing: autoScreenshareService.isProcessing(),
        isPrepareLesson,
        isCancelModalOpen: autoScreenshareService.isCancelModalOpen(),
      },
    }, `[SCREENSHARE DEBUG] Sharing effect — isSharing=${isSharing}, prev=${previousScreenshareStatus}`);

    // Clear any pending stop debounce when isSharing changes
    if (stopDebounceRef.current) {
      clearTimeout(stopDebounceRef.current);
      stopDebounceRef.current = null;
    }

    // Check if screenshare status changed from false to true (manually started) — immediate
    if (!previousScreenshareStatus && isSharing) {
      logger.info({
        logCode: 'auto_screenshare_manual_start_detected',
        extraInfo: { userId: user.userId, userName: user.name },
      }, `Screenshare manually started, starting recording for moderator: ${user.name}`);

      autoScreenshareService.handleScreenshareManualStart(user);
      setPreviousScreenshareStatus(true);
    }

    // Check if screenshare status changed from true to false — debounce 500ms
    // This prevents false stop triggers during WebRTC reconnection flicker
    if (previousScreenshareStatus && !isSharing) {
      logger.debug({
        logCode: 'auto_screenshare_stop_debounce_start',
        extraInfo: { userId: user.userId, userName: user.name },
      }, `Screenshare went false, debouncing 500ms before treating as stop`);

      stopDebounceRef.current = setTimeout(() => {
        stopDebounceRef.current = null;
        // [Fix #4] Read fresh user from ref instead of stale closure
        const freshUser = userRef.current;
        if (!freshUser) return;
        // Re-check using the non-hook function (safe outside render)
        if (!isSharingFn()) {
          logger.info({
            logCode: 'auto_screenshare_manual_stop_detected',
            extraInfo: { userId: freshUser.userId, userName: freshUser.name },
          }, `Screenshare manually stopped (confirmed after 500ms), stopping recording for moderator: ${freshUser.name}`);

          autoScreenshareService.handleScreenshareManualStop(freshUser);
          setPreviousScreenshareStatus(false);
        } else {
          logger.info({
            logCode: 'auto_screenshare_flicker_ignored',
            extraInfo: { userId: freshUser.userId, userName: freshUser.name },
          }, `Screenshare false was transient (reconnect flicker), ignoring`);
          setPreviousScreenshareStatus(true);
        }
      }, 500);

      // Don't update previousScreenshareStatus yet — wait for debounce
      return;
    }

    // Close modal if screenshare becomes active and modal is open
    if (isSharing && autoScreenshareService.isCancelModalOpen()) {
      logger.info({ logCode: 'screenshare_active_closing_modal' }, 'Screenshare became active - closing modal');
      autoScreenshareService.hideCancelModal();
    }

    // Update previous screenshare status for non-debounced cases
    if (previousScreenshareStatus !== isSharing) {
      setPreviousScreenshareStatus(isSharing);
    }
  }, [isSharing, currentUserData, previousScreenshareStatus, isPrepareLesson]);

  // Monitor external video status changes
  useEffect(() => {
    const user = currentUserData?.user_current?.[0];
    if (!user || !user.isModerator) return;
    // [Fix N4] Use service.isProcessing()
    if (autoScreenshareService.isProcessing()) return;

    if (isPrepareLesson) {
      return;
    }

    const currentHasExternalVideo = currentMeeting?.componentsFlags?.hasExternalVideo ?? false;

    // Check if external video status changed from true to false (user manually stopped sharing)
    if (previousHasExternalVideo && !currentHasExternalVideo) {
      logger.info({
        logCode: 'auto_screenshare_external_video_stopped',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
        },
      }, `External video stopped for moderator: ${user.name}, triggering stop sharing and showing modal`);

      // Trigger stop sharing only if screenshare is actually active
      if (isSharing) {
        screenshareHasEnded();
      }

      // Show the screen share modal again
      autoScreenshareService.showCancelModal(user,
        () => autoScreenshareService.retryScreenshare(user),
        () => {
          autoScreenshareService.hasStartedScreenshare = false;
          autoScreenshareService.isProcessingScreenshare = false;
          autoScreenshareService.hideCancelModal();
        },
        'manualStop'
      );
    }

    // Update previous external video status
    setPreviousHasExternalVideo(currentHasExternalVideo);
  }, [currentMeeting, currentUserData, previousHasExternalVideo, isPrepareLesson]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (stopDebounceRef.current) {
        clearTimeout(stopDebounceRef.current);
      }
    };
  }, []);

  return null;
};

export default AutoScreenshareHandler;
